import type { CSSProperties, MouseEventHandler } from 'react';

/**
 * Miniatura de foto de PRODUTO em lista/tabela/modal.
 *
 * Por que existe (DA-526): a foto de produto aparece em miniatura em ~19 telas e cada
 * uma escrevia o proprio `<img>` na mao, copiado da vizinha — 28 copias com
 * `objectFit: 'cover'`. `cover` CORTA pra preencher o quadrado, entao perfil, cabo e
 * barra apareciam como uma faixa do meio, sem o rotulo que identifica o produto
 * (medido em prod: 614 das 1.316 fotos nao sao quadradas, 34 delas de 1,5x pra cima).
 * A regra certa ja existia na classe `.produto-thumb` do styles.css desde 08/05 e era
 * justamente a que as copias nao conheciam — quem usou a peca herdou o conserto, quem
 * copiou a linha nao. Consertar as 28 copias sem criar a peca deixaria a 29a tela
 * nascer errada de novo.
 *
 * A classe traz `object-fit: contain` + **fundo branco**. O branco nao e enfeite: a
 * derivada sai achatada em branco pelo servidor, entao sem ele a sobra do `contain`
 * fica da cor do painel e a emenda le como "o fundo ficou em duas cores" — foi a 4a
 * camada da [DA-525].
 *
 * ⛔ NAO usar em foto de CONTATO (avatar redondo do WhatsApp, recibo, perfil da inbox):
 * la o `cover` e o certo — rosto dentro de circulo se corta, nao se encolhe.
 *
 * Sem foto, cada tela desenha o proprio vazio (icone, traco, moldura tracejada) — o
 * componente cobre so o caso COM foto, que e onde estava o defeito.
 */
export function FotoProduto({
  url,
  tam = 36,
  style,
  onClick,
  title,
  lazy = true,
}: {
  url: string;
  /** lado do quadrado. Numero = px; string pra quem calcula o tamanho (o orcamento
   *  impresso deixa o operador escolher a escala da foto). */
  tam?: number | string;
  /** extras da tela: margem, cursor, alinhamento. Vence a classe por ser inline. */
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLImageElement>;
  title?: string;
  lazy?: boolean;
}) {
  return (
    <img
      src={url}
      alt=""
      title={title}
      loading={lazy ? 'lazy' : undefined}
      onClick={onClick}
      className="produto-thumb"
      /**
       * O tamanho e um TETO, nao uma promessa — e por isso a altura sai da proporcao,
       * nao de um numero cravado. O preflight do Tailwind poe `max-width: 100%` em
       * toda `<img>`, entao numa celula estreita a largura pedida e cortada calada:
       * medido em prod na "O que falta comprar", um `tam={46}` virou **32 de largura
       * e 46 de altura** — caixa retangular, com o produto no meio de uma faixa
       * branca. Cravar `maxWidth: none` pra forcar os 46 alargaria a coluna e
       * empurraria a tabela pra fora no tablet, que e o defeito da [DA-456]; entao a
       * saida e ceder na largura e **manter o quadrado**: `height: auto` (inline,
       * senao o `height: 50px` da propria classe vence) + `aspectRatio` 1.
       * Conferido na tela: 32x46 -> 32x32, com a tabela na mesma largura.
       * `alignSelf` fecha o ultimo buraco: em container flex, altura `auto` seria
       * ESTICADA pelo `stretch` padrao.
       */
      style={{ width: tam, height: 'auto', aspectRatio: '1 / 1', alignSelf: 'center', display: 'block', ...style }}
    />
  );
}
